/**
 * PostgREST `select=` parsing.
 *
 * Supported shapes (all of them appear in this app):
 *   `*`
 *   `id, job_number, created_at`
 *   `jobs ( job_number )` / `jobs(job_number)`
 *   `jobs ( job_number, customer_id, customers ( name ) )`   two levels
 *   `jobs!inner(customer_id)`                                inner join
 *   `profiles:profiles(display_name)`                        aliased embed
 *   `*, compliance_workflows(name)`                          star + embed
 */
import { splitTopLevel } from './filters';

export type SelectField =
  | { kind: 'col'; name: string }
  | { kind: 'star' }
  | { kind: 'embed'; alias: string; table: string; inner: boolean; fields: SelectField[] };

export type EmbedField = Extract<SelectField, { kind: 'embed' }>;

export type ParsedSelect = {
  star: boolean;
  cols: string[];
  embeds: EmbedField[];
};

const cache = new Map<string, ParsedSelect>();

function parseHead(head: string): { alias: string; table: string; inner: boolean } {
  let name = head.trim();
  let alias: string | null = null;
  const colon = name.indexOf(':');
  if (colon >= 0) {
    alias = name.slice(0, colon).trim();
    name = name.slice(colon + 1).trim();
  }
  // `table!inner`, `table!left`, `table!fk_name`
  const bangs = name.split('!').map((p) => p.trim());
  const table = bangs[0] ?? '';
  const inner = bangs.slice(1).some((m) => m.toLowerCase() === 'inner');
  return { alias: alias ?? table, table, inner };
}

function parseFields(input: string): SelectField[] {
  const out: SelectField[] = [];
  for (const part of splitTopLevel(input)) {
    const open = part.indexOf('(');
    if (open >= 0 && part.trimEnd().endsWith(')')) {
      const head = part.slice(0, open);
      const body = part.trimEnd().slice(open + 1, -1);
      const { alias, table, inner } = parseHead(head);
      if (!table) continue;
      out.push({ kind: 'embed', alias, table, inner, fields: parseFields(body) });
      continue;
    }
    const name = part.trim();
    if (!name) continue;
    if (name === '*') { out.push({ kind: 'star' }); continue; }
    // A rename (`alias:col`) or a cast (`col::text`) — neither is used in this
    // app; keep the bare column name so the value still comes through.
    const colon = name.indexOf(':');
    out.push({ kind: 'col', name: colon >= 0 ? name.slice(colon + 1).replace(/^:/, '').trim() : name });
  }
  return out;
}

export function fieldsToParsed(fields: SelectField[]): ParsedSelect {
  return {
    star: fields.some((f) => f.kind === 'star'),
    cols: fields.flatMap((f) => (f.kind === 'col' ? [f.name] : [])),
    embeds: fields.flatMap((f) => (f.kind === 'embed' ? [f] : [])),
  };
}

export function parseSelect(input: string | undefined | null): ParsedSelect {
  const key = input == null || input.trim() === '' ? '*' : input;
  const hit = cache.get(key);
  if (hit) return hit;

  const parsed = fieldsToParsed(parseFields(key));
  cache.set(key, parsed);
  return parsed;
}
